// new ia- component in the devkit's conventions (the kit has no gift component): keeps the mini cart's one free gift, the best earned gift of every gift block, and the cards to swap or add the others
import { EVENTS, subscribe } from "ia-util-pubsub";
import { badgeCount } from "ia-cart-api";

// The gifts the shopper turned down (block ids): the one they removed, or the better ones they
// swapped away from. Kept so the choice holds on the next page too; cleared once nothing the
// shopper chose is left in the cart (an emptied cart, the empty cart after an order). The
// section's cart JSON carries no cart token to key them by.
const DECLINED_KEY = "ia-cart-gifts-declined";
// Landing pages visited ({ page path: time }), for the landing-page gifts
const LANDING_KEY = "ia-cart-gifts-landing";
const LANDING_DAYS = 7;
// New customer: within this many days of the first visit (the cookie the theme's custom.js sets)
const NEW_CUSTOMER_DAYS = 3;
const DAY_MS = 24 * 60 * 60 * 1000;
// Gifts the cart charged for (block ids), for this browser tab: the gift app was off or the
// product lost its gwp tag. Never added again in the tab, so add and remove cannot alternate.
const CHARGED_KEY = "ia-cart-gifts-charged";
// A charged gift is removed when it is still charged on a cart read at least this long after the
// first: a price the store has not settled yet is not a reason to take the gift away.
const CHARGED_GRACE_MS = 3000;
const DEFAULT_PRIORITY = 10;

function readStored(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch (error) {
    return fallback;
  }
}

function writeStored(key, value, keep, storage = localStorage) {
  try {
    if (keep) storage.setItem(key, JSON.stringify(value));
    else storage.removeItem(key);
  } catch (error) {
    // blocked storage: it holds for this page only
  }
}

function readSession(key) {
  try {
    return JSON.parse(sessionStorage.getItem(key)) ?? [];
  } catch (error) {
    return [];
  }
}

/** A page as the landing gifts compare it: the path, without the market's prefix (/en-ca) or a trailing slash. */
function pagePath(value) {
  let path;
  try {
    path = new URL(value, window.location.origin).pathname.toLowerCase().replace(/\/+$/, "");
  } catch (error) {
    return "";
  }
  const root = (window.Shopify?.routes?.root || "/").toLowerCase().replace(/\/+$/, "");
  if (root && (path === root || path.startsWith(`${root}/`))) path = path.slice(root.length);
  return path;
}

function isUnder(path, page) {
  return path === page || path.startsWith(`${page}/`);
}

function isNewCustomer() {
  const match = document.cookie.match(/(?:^|; )first_visit_time=([^;]+)/);
  const since = match ? Number.parseInt(decodeURIComponent(match[1]), 10) : Number.NaN;
  // No cookie yet: this is the first visit (custom.js sets it), as the old gift script read it
  return Number.isNaN(since) || Date.now() - since <= NEW_CUSTOMER_DAYS * DAY_MS;
}

/** Lower priority number first; equal numbers: the higher minimum cart value, then the order of the blocks. */
function byRank(a, b) {
  return a.priority - b.priority || b.minValue - a.minValue || a.order - b.order;
}

/**
 * The cart's free gift. Every gift block of the Cart gifts block (_ia-cart-gift-*) renders an
 * anchor (snippets/ia-cart-gift-anchor.liquid); the progress bar's gift goals only show progress.
 * An anchor carries [data-gift-source] (its block id, stamped on the gift line as _gwp_source),
 * [data-gift-variant], [data-gift-priority], [data-gift-min-value],
 * [data-gift-available], [data-gift-earned] when every condition Liquid can see is met, the
 * browser's conditions ([data-gift-landing], [data-gift-new-customer]) and the gift's card as a
 * <template data-gift-card>.
 *
 * This element is the only thing that adds or removes gift lines:
 * - only on a server answer, with the anchors of that same render: a gift granted on a
 *   prediction would be a real line for a cart the server has not agreed to;
 * - one gift at a time: of the gifts earned, the best ranked (byRank) the shopper has not
 *   turned down. A better gift earned later replaces it; a gift no longer earned goes;
 * - while another gift is earned, the gift row shows Swap: it removes the gift, like the row's
 *   remove. Removing the gift brings no other in its place: every earned gift then shows a card
 *   to "Add" instead;
 * - lines of a gift block that is gone or hidden, and the old gift script's lines (_free_gift),
 *   are removed.
 * The price is the gift app's (beekeepers-gwp zeroes a marked line of a gwp-tagged product). A
 * gift the cart still charges for is removed and not added again in the tab (isCharged).
 */
class IaCartGifts extends HTMLElement {
  connectedCallback() {
    this.offers = this.querySelector("[data-gift-offers]");
    this.addLabel = this.dataset.addLabel || "Add";
    // A gift the store refused to add is not asked for again on this page: retrying on every
    // answer would loop (refusal, re-read, answer, retry)
    this.refused ||= new Set();
    this.charged ||= new Set(readSession(CHARGED_KEY));
    this.declined ||= new Set(readStored(DECLINED_KEY, []));
    this.campaigns ||= this.readCampaigns(this.scope);
    this.root ||= this.scope;
    this.recordLanding(this.campaigns);

    // Paired with disconnectedCallback: the drawer's move to <body> reconnects this element
    this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
    this.unsubscribers = [
      subscribe(EVENTS.cartChanged, (event) => {
        const { cartObj, cartDom } = event.detail || {};
        if (!cartDom) return;
        // On every answer, even while a pass is busy and parks it: the bar paints from this answer
        this.markEarnedFrom(cartObj, cartDom);
        this.reconcile(cartObj, cartDom);
      }),
      subscribe(EVENTS.cartGiftDeclined, (event) => this.decline(event.detail?.source)),
    ];
    this.abort?.abort();
    this.abort = new AbortController();
    this.addEventListener("click", this.handleClick.bind(this), { signal: this.abort.signal });
    // The gift row's Swap sits in the cart lines, outside this element
    this.drawer = this.closest("ia-cart");
    this.drawer?.addEventListener("click", this.handleSwap.bind(this), { signal: this.abort.signal });
    if (window.iaCartAPI?.cart) this.markEarnedFrom(window.iaCartAPI.cart, this.scope);

    // Once per page as well, for a cart that qualifies with no change made here (a reload,
    // another tab). After the seed check: a gift the server holds and a stale page lacks would
    // otherwise be added twice.
    const api = window.iaCartAPI;
    api?.verified.then(() => this.reconcile(api.cart, this.scope));
  }

  disconnectedCallback() {
    this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
    this.unsubscribers = null;
    this.abort?.abort();
    clearTimeout(this.chargedRecheck);
    this.chargedRecheck = null;
  }

  get scope() {
    return this.closest("ia-cart") || document;
  }

  /** The gift blocks of one render: the live drawer, or a section render the API parsed. */
  readCampaigns(root) {
    const campaigns = new Map();
    root.querySelectorAll("[data-gift-source]").forEach((anchor) => {
      const source = anchor.dataset.giftSource;
      if (!source || campaigns.has(source)) return;
      const priority = Number.parseFloat(anchor.dataset.giftPriority);
      campaigns.set(source, {
        source,
        variant: anchor.dataset.giftVariant || "",
        available: anchor.hasAttribute("data-gift-available"),
        earned: anchor.hasAttribute("data-gift-earned"),
        priority: Number.isNaN(priority) ? DEFAULT_PRIORITY : priority,
        minValue: Number(anchor.dataset.giftMinValue) || 0,
        order: campaigns.size,
        landing: anchor.hasAttribute("data-gift-landing")
          ? anchor.dataset.giftLanding.split(/[\s,]+/).map(pagePath).filter(Boolean)
          : null,
        newCustomer: anchor.hasAttribute("data-gift-new-customer"),
        card: anchor.querySelector("template[data-gift-card]"),
        row: anchor.querySelector("template[data-gift-row]"),
      });
    });
    return [...campaigns.values()];
  }

  /** Notes a visit to a landing gift's page: the gift holds for LANDING_DAYS on this device. */
  recordLanding(campaigns) {
    const path = pagePath(window.location.href);
    const pages = campaigns.flatMap((campaign) => campaign.landing || []).filter((page) => isUnder(path, page));
    if (!pages.length) return;
    const visits = this.freshVisits();
    pages.forEach((page) => {
      visits[page] = Date.now();
    });
    writeStored(LANDING_KEY, visits, true);
  }

  freshVisits() {
    const visits = readStored(LANDING_KEY, {});
    return Object.fromEntries(Object.entries(visits).filter(([, time]) => Date.now() - time <= LANDING_DAYS * DAY_MS));
  }

  /** Earned (Liquid), on sale, and the browser's own conditions met. */
  isEligible(campaign, visits) {
    if (!campaign.earned || !campaign.available || !campaign.variant) return false;
    if (campaign.newCustomer && !isNewCustomer()) return false;
    if (campaign.landing && !campaign.landing.some((page) => page in visits)) return false;
    return true;
  }

  ranked(campaigns) {
    const visits = this.freshVisits();
    return campaigns.filter((campaign) => this.isEligible(campaign, visits)).sort(byRank);
  }

  saveDeclined() {
    writeStored(DECLINED_KEY, [...this.declined], this.declined.size > 0);
  }

  /**
   * Makes the cart's gift lines match the gift blocks: one write for the removals, one for the
   * grant. One pass at a time; an answer that lands mid-pass is kept (the newest only) and
   * reconciled next, and a cart that already matches makes no write, so it converges.
   */
  async reconcile(cart, root) {
    const api = window.iaCartAPI;
    if (!api || !cart?.items || !root) return;
    if (this.reconciling) {
      this.missed = { cart, root };
      return;
    }
    this.reconciling = true;
    // A held checkout waits for the gift writes as well (removal then grant are two writes)
    api.writeStarted();
    try {
      const campaigns = this.readCampaigns(root);
      this.campaigns = campaigns;
      this.root = root;
      // Nothing the shopper chose is left: the cart starts over, declines included
      if (this.declined.size && badgeCount(cart) === 0) {
        this.declined.clear();
        this.saveDeclined();
      }

      const ranked = this.ranked(campaigns);
      const rank = new Map(ranked.map((campaign, index) => [campaign.source, index]));
      const bySource = new Map(campaigns.map((campaign) => [campaign.source, campaign]));
      const updates = {};
      let kept = null;
      cart.items.forEach((item) => {
        const props = item.properties || {};
        // The old gift script's lines: the script is switched off and its gifts go with it
        if (props._free_gift) {
          updates[item.key] = 0;
          return;
        }
        const source = props._gwp_source;
        if (!source) return;
        const campaign = bySource.get(source);
        // A block gone or hidden, a variant the block no longer names (the merchant changed
        // it), a gift no longer earned, or one the shopper turned down
        if (!campaign || String(item.variant_id) !== campaign.variant || !rank.has(source) || this.declined.has(source)) {
          updates[item.key] = 0;
          return;
        }
        // One gift line: the best ranked stays
        if (kept && rank.get(kept.source) <= rank.get(source)) {
          updates[item.key] = 0;
          return;
        }
        if (kept) updates[kept.line.key] = 0;
        kept = { source, line: item };
      });

      if (this.isCharged(kept)) {
        updates[kept.line.key] = 0;
        kept = null;
      }

      const best = ranked.find((campaign) => this.isOffered(campaign.source));
      let grant = null;
      if (best && (!kept || rank.get(best.source) < rank.get(kept.source))) {
        if (kept) updates[kept.line.key] = 0;
        kept = null;
        grant = best;
      }
      if (kept?.line.quantity > 1) updates[kept.line.key] = 1;

      this.renderOffers(ranked, kept?.source || grant?.source || null);

      if (Object.keys(updates).length) await api.updateCart({ data: { updates }, silent: true });
      if (grant) await this.grant(grant);
    } catch (error) {
      console.error(">>> gift reconcile error:", error);
    } finally {
      this.reconciling = false;
      api.writeDone();
      const missed = this.missed;
      this.missed = null;
      if (missed) this.reconcile(missed.cart, missed.root);
      // The pass that adds a picked gift is over: its line is in the answer, or it was refused
      else this.dropPendingRow();
    }
  }

  /** Not turned down, not refused by the store, not charged for in this tab. */
  isOffered(source) {
    return !this.declined.has(source) && !this.refused.has(source) && !this.charged.has(source);
  }

  /**
   * The safety net behind the gift app: a gift line that costs money (the app is off, the
   * product lost its gwp tag) leaves the cart. Two reads at least CHARGED_GRACE_MS apart must
   * both show the price; the second read is one cart refresh, scheduled once. A removed gift is
   * marked charged for the tab, so no pass grants it again: one removal, no add/remove cycle.
   */
  isCharged(kept) {
    if (!(kept?.line.final_price > 0)) {
      this.chargedSince = null;
      return false;
    }
    const seen = this.chargedSince;
    if (seen?.key === kept.line.key && Date.now() - seen.time >= CHARGED_GRACE_MS) {
      this.chargedSince = null;
      this.charged.add(kept.source);
      writeStored(CHARGED_KEY, [...this.charged], true, sessionStorage);
      console.warn(`>>> gift ${kept.source} costs ${kept.line.final_price} in the cart: removed (is the gift app on, the product tagged gwp?)`);
      return true;
    }
    if (seen?.key !== kept.line.key) this.chargedSince = { key: kept.line.key, time: Date.now() };
    if (!this.chargedRecheck) {
      this.chargedRecheck = setTimeout(() => {
        this.chargedRecheck = null;
        window.iaCartAPI?.refresh();
      }, CHARGED_GRACE_MS);
    }
    return false;
  }

  /** The gift the shopper picked is added loudly: the store's reason shows if it is refused. */
  async grant(campaign) {
    const api = window.iaCartAPI;
    const loud = this.picked === campaign.source;
    if (loud) this.picked = null;
    const cart = await api.addToCartJson(
      { items: [{ id: Number(campaign.variant), quantity: 1, properties: { _gwp_source: campaign.source } }] },
      { silent: !loud },
    );
    if (!cart) {
      this.refused.add(campaign.source);
      // A refused add publishes no cart change: the pass that grants the next gift is asked for here
      this.missed ||= { cart: api.cart, root: this.root };
    }
  }

  /**
   * The shopper removed their gift: no other comes in its place. Every gift earned now is
   * turned down, and the cards ("Add") show on the tap.
   */
  decline(source) {
    if (!source) return;
    const ranked = this.ranked(this.campaigns);
    ranked.forEach((campaign) => this.declined.add(campaign.source));
    this.declined.add(source);
    this.saveDeclined();
    this.renderOffers(ranked, null);
  }

  handleClick(event) {
    const button = event.target.closest("[data-gift-pick]");
    if (button) this.pick(button.dataset.giftPick);
  }

  /**
   * Swap on the gift row: the same as removing the gift (the row's own remove, so the row
   * leaves on the tap and a refusal brings it back), which shows the earned gifts to pick from.
   */
  handleSwap(event) {
    const button = event.target.closest("[data-gift-swap]");
    if (!button) return;
    button.closest("ia-cart-item")?.querySelector("[data-cart-item-remove]")?.click();
  }

  /**
   * The shopper picked a gift from its card: every better gift is turned down (so none replaces
   * the pick), the picked one is not, and the next pass swaps the cart's gift for it.
   */
  pick(source) {
    const api = window.iaCartAPI;
    const ranked = this.ranked(this.campaigns);
    const index = ranked.findIndex((campaign) => campaign.source === source);
    if (!api?.cart || index < 0) return;
    ranked.slice(0, index).forEach((campaign) => this.declined.add(campaign.source));
    this.declined.delete(source);
    this.refused.delete(source);
    this.saveDeclined();
    this.picked = source;
    this.showPendingRow(ranked[index]);
    // The lines as they will be: a removal still on its way (the Swap just before) is not removed twice
    this.reconcile({ ...api.cart, items: api.predictedLines() }, this.root);
  }

  /** The picked gift's line, on the tap: the store's answer re-renders the lines without it. */
  showPendingRow(campaign) {
    this.dropPendingRow();
    const row = campaign.row?.content.firstElementChild;
    const list = this.drawer?.querySelector("[data-cart-list]");
    if (!row || !list) return;
    this.pendingRow = document.importNode(row, true);
    list.prepend(this.pendingRow);
    if (this.offers) this.offers.hidden = true;
  }

  dropPendingRow() {
    this.pendingRow?.remove();
    this.pendingRow = null;
  }

  /** A gift the shopper can be shown a card for: it has one, and the store neither refused nor charged it. */
  isOfferable(campaign) {
    return Boolean(campaign.card) && !this.refused.has(campaign.source) && !this.charged.has(campaign.source);
  }

  /**
   * A gift is in the cart or one tap away: the progress bar's gift goal has nothing more to give.
   * Repaints the bar only on a change; the bar reads the mark and writes nothing back, so no loop.
   */
  markEarned(earned) {
    const value = String(earned);
    if (!this.drawer || this.drawer.getAttribute("data-gift-earned") === value) return;
    this.drawer.setAttribute("data-gift-earned", value);
    // render?.: the bar's element is defined by ia-cart.js, which may still be on its way
    this.drawer.querySelector("ia-cart-tier")?.render?.();
  }

  /** The same mark from a cart and its render, before any write: a gift line still earned, or a card to show. */
  markEarnedFrom(cart, root) {
    const ranked = this.ranked(this.readCampaigns(root));
    const earnedSources = new Set(ranked.map((campaign) => campaign.source));
    const held = (cart?.items || []).some((item) => {
      const source = item.properties?._gwp_source;
      return Boolean(source) && earnedSources.has(source) && !this.declined.has(source) && !this.charged.has(source);
    });
    this.markEarned(held || ranked.some((campaign) => this.isOfferable(campaign)));
  }

  /**
   * With a gift in the cart: no cards, and the drawer is marked data-gifts-swappable while
   * another gift is earned (the gift row's Swap shows, ia-cart.css). Without one: an "Add" card
   * for every earned gift. A card already shown is kept as it is, so its image never reloads.
   * The drawer's data-gift-earned says whether a gift is held or offered ("true"/"false"; the
   * progress bar's gift goal reads it).
   */
  renderOffers(ranked, held) {
    if (!this.offers) return;
    const offerable = ranked.filter((campaign) => this.isOfferable(campaign));
    this.drawer?.toggleAttribute("data-gifts-swappable", Boolean(held) && offerable.some((campaign) => campaign.source !== held));
    this.markEarned(Boolean(held) || offerable.length > 0);
    const mounted = new Map([...this.offers.children].map((card) => [card.dataset.giftCardSource, card]));
    const cards = (held ? [] : offerable)
      .map((campaign) => mounted.get(campaign.source) || document.importNode(campaign.card.content, true).firstElementChild)
      .filter(Boolean);
    cards.forEach((card) => {
      const button = card.querySelector("[data-gift-pick]");
      if (!button || button.textContent === this.addLabel) return;
      button.textContent = this.addLabel;
      button.setAttribute("aria-label", `${this.addLabel}: ${button.dataset.giftTitle || ""}`);
    });
    const same = cards.length === mounted.size && cards.every((card, index) => this.offers.children[index] === card);
    if (!same) this.offers.replaceChildren(...cards);
    this.offers.hidden = cards.length === 0;
  }
}

if (!customElements.get("ia-cart-gifts")) customElements.define("ia-cart-gifts", IaCartGifts);
